import { useState, useEffect } from 'react';
import { ticketApi } from '../../api/ticket.api';
import { useWorkspaceNav } from '../../store/WorkspaceContext';
import { Badge, EmptyState, PageHeader, Skeleton } from '../../components/ui/index';
import { toPersian, timeAgo } from '../../utils/persianNumber';
import { useDebounce } from '../../hooks/useDraft';
import TicketFilterBar from '../../components/tickets/TicketFilterBar';
import { useTicketFilters } from '../../hooks/useTicketFilters';
import TicketBodyPreview from '../../components/tickets/TicketBodyPreview';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

// Filter keys here must match KNOWN_FILTERS in the backend's
// queryGetTicketListView (dispatcher_seen / responsed / responsing / ...).
// The previous 'ticket_type'/'employee_type' params sent by this page were
// not read by the backend at all, so these tabs were silent no-ops and
// every visit returned the same unfiltered list.
const TYPES = [
  { id: 'unseen',    label: 'دیده‌نشده',    params: { dispatcher_seen: 0 } },
  { id: 'responsing',label: 'در حال بررسی', params: { responsing: 1 } },
];

export default function TicketListPage({ mode = 'active' }) {
  const workspaceNav = useWorkspaceNav();
  const [ticketType, setTicketType] = useState('unseen');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const isResponded = mode === 'responded';

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    const params = isResponded
      ? { dispatcher_seen: 1, responsed: 1 }
      : (TYPES.find((t) => t.id === ticketType)?.params || {});
    return ticketApi.getTicketListView(params)
      .then((res) => setTickets(Array.isArray(res.data) ? res.data : res.data?.tickets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [ticketType, isResponded]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load(false), { intervalMs: 10000 });

  // This page is employee-only (queue/list views reached from the
  // sidebar), so the company combo box is always relevant here - unlike
  // UserDashboard, which is also used by customer roles.
  const { filteredSorted, company, setCompany, sortBy, setSortBy, companyOptions } =
    useTicketFilters(tickets, { companyKey: 'company', dateKey: 'send_date' });

  const filtered = filteredSorted.filter((t) =>
    !debouncedSearch || (t.ticket_title || t.title || '').includes(debouncedSearch) || (t.ticket_text || '').includes(debouncedSearch)
  );

  return (
    <div className="fade-in">
      <PageHeader title={isResponded ? 'تیکت‌های پاسخ داده‌شده' : 'همه'} subtitle={`${toPersian(filtered.length)} تیکت`} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {!isResponded && TYPES.map((t) => (
          <button key={t.id} onClick={() => setTicketType(t.id)} style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${ticketType === t.id ? '#3b82f6' : '#e2e8f0'}`, background: ticketType === t.id ? '#eff6ff' : '#fff', color: ticketType === t.id ? '#2563eb' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
        <TicketFilterBar
          sortBy={sortBy} setSortBy={setSortBy}
          company={company} setCompany={setCompany}
          companyOptions={companyOptions}
        />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جستجو..." style={{ marginRight: 'auto', padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200 }} />
      </div>

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3,4].map((k) => <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}><Skeleton h={18} w="55%" style={{ marginBottom: 8 }} /><Skeleton h={14} w="80%" /></div>)}</div>
        : filtered.length === 0
          ? <EmptyState icon="📭" title="تیکتی یافت نشد" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((t, i) => (
                <div key={t.id || i} onClick={() => workspaceNav(`/tickets/${t.id || t.ticket_id}/process`, t.ticket_title || t.title || 'تیکت')} className="pj-row-card" style={{ background: '#fff', borderRadius: 12, borderWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0', padding: '14px 20px', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(59,130,246,0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>🎫</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.ticket_title || t.title}</div>
                      <TicketBodyPreview ticket={t} style={{ margin: '4px 0 0' }} />
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(t.sender_first_name || t.sender_last_name) && (
                          <span>👤 {[t.sender_first_name, t.sender_last_name].filter(Boolean).join(' ')}{t.sender_username ? ` (${t.sender_username})` : ''}</span>
                        )}
                        {t.company && <span>🏢 {t.company_display || t.company}</span>}
                        <span>{timeAgo(t.send_date)}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {t.is_guest && <Badge variant="amber">مهمان</Badge>}
                    {t.is_active && <Badge variant="blue">فعال</Badge>}
                    <span style={{ color: '#94a3b8', fontSize: 18 }}>›</span>
                  </div>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );
}
